import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { MODELS, MODEL_KEYS, ModelBadge } from "../components/ModelBadge";
import { MessageSquare, GitCompare, Bot, BookOpen, FileText, ArrowRight, AlertTriangle, Sparkles } from "lucide-react";

const QUICK_ACTIONS = [
  { to: "/chat",      icon: MessageSquare, title: "Chat",     desc: "Talk to a single model",   bg: "#DBEAFE", border: "#2563EB" },
  { to: "/compare",   icon: GitCompare,    title: "Compare",  desc: "Side-by-side answers",     bg: "#FEE2E2", border: "#DC2626" },
  { to: "/agents",    icon: Bot,           title: "Agents",   desc: "Build custom AI agents",   bg: "#D1FAE5", border: "#059669" },
  { to: "/research",  icon: BookOpen,      title: "Research", desc: "Multi-model synthesis",    bg: "#FEF3C7", border: "#D97706" },
  { to: "/documents", icon: FileText,      title: "Documents",desc: "Upload & analyze files",   bg: "#E0E7FF", border: "#4F46E5" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [keyConfigured, setKeyConfigured] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, a, m] = await Promise.all([
          api.get("/conversations"),
          api.get("/agents"),
          api.get("/models"),
        ]);
        setConvs(c.data.conversations || []);
        setAgents(a.data.agents || []);
        setKeyConfigured(m.data.api_key_configured);
      } catch {}
    })();
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="dashboard">
      {/* Welcome */}
      <div className="brut-card p-6 md:p-8 mb-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-200 opacity-40 float-y" />
        <div className="absolute right-20 -bottom-10 w-32 h-32 rounded-full bg-emerald-200 opacity-40 float-y" style={{ animationDelay: "1.5s" }} />
        <div className="relative">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
            Welcome back
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-3">
            Hey, {user?.name?.split(" ")[0] || "friend"} 👋
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">
            Pick a tool. Mix 4 minds — DeepSeek, Qwen, Kimi & Llama — into whatever you're building today.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {MODEL_KEYS.map((k) => <ModelBadge key={k} id={k} size="sm" />)}
          </div>
        </div>
      </div>

      {!keyConfigured && (
        <div className="brut-card p-4 mb-6 flex items-start gap-3 bg-amber-50" data-testid="api-key-warning">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
          {user?.is_admin ? (
            <div className="text-sm">
              <div className="font-bold font-heading mb-0.5">No OpenRouter key set yet</div>
              <div className="text-slate-600">The app is running in demo mode. Add <code className="bg-slate-100 px-1 rounded">OPENROUTER_API_KEY</code> to <code className="bg-slate-100 px-1 rounded">/app/backend/.env</code> and restart the backend to enable real model responses. Get a key at <a className="underline font-semibold" href="https://openrouter.ai/keys" target="_blank" rel="noreferrer">openrouter.ai/keys</a>.</div>
            </div>
          ) : (
            <div className="text-sm">
              <div className="font-bold font-heading mb-0.5">Model responses are paused</div>
              <div className="text-slate-600">We're finishing setup. Responses shown right now are samples, not real model answers.</div>
            </div>
          )}
        </div>
      )}

      {/* Quick actions */}
      <h2 className="font-heading text-xl font-bold mb-3">Quick actions</h2>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="quick-actions">
        {QUICK_ACTIONS.map(({ to, icon: I, title, desc, bg, border }) => (
          <Link
            key={to}
            to={to}
            data-testid={`quick-${title.toLowerCase()}`}
            className="brut-card p-4 group flex flex-col gap-2 min-h-[140px]"
          >
            <div
              className="w-10 h-10 rounded-xl border-2 flex items-center justify-center"
              style={{ backgroundColor: bg, borderColor: border, color: border }}
            >
              <I size={20} strokeWidth={2.5} />
            </div>
            <div className="font-heading font-bold mt-auto">{title}</div>
            <div className="text-xs text-slate-500 font-medium leading-tight">{desc}</div>
            <ArrowRight size={14} className="text-slate-400 group-hover:text-slate-900 mt-1" />
          </Link>
        ))}
      </div>

      {/* Recent + Agents */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="brut-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold">Recent chats</h3>
            <Link to="/chat" className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900">New →</Link>
          </div>
          {convs.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center italic">No chats yet. Start one!</div>
          ) : (
            <div className="space-y-2" data-testid="recent-convs">
              {convs.slice(0, 6).map((c) => (
                <Link
                  key={c.conversation_id}
                  to={
                    c.kind === "compare" ? `/compare?c=${c.conversation_id}` :
                    c.kind === "research" ? `/research?c=${c.conversation_id}` :
                    c.kind === "document" ? `/documents?c=${c.conversation_id}` :
                    c.kind === "agent" ? `/agents/run?c=${c.conversation_id}` :
                    `/chat?c=${c.conversation_id}`
                  }
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 border-2 border-transparent hover:border-slate-900"
                >
                  <div className="flex items-center gap-2 truncate">
                    <span>{c.kind === "compare" ? "⚖️" : c.kind === "agent" ? "🤖" : c.kind === "research" ? "🔬" : c.kind === "document" ? "📄" : "💬"}</span>
                    <span className="truncate font-semibold text-sm">{c.title}</span>
                  </div>
                  {c.model && <ModelBadge id={c.model} size="sm" showName={false} />}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="brut-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold">Your agents</h3>
            <Link to="/agents" className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900">Build →</Link>
          </div>
          {agents.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center italic flex flex-col items-center gap-2">
              <Sparkles size={28} className="text-slate-300" />
              No agents yet — build one!
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2" data-testid="recent-agents">
              {agents.slice(0, 6).map((a) => (
                <Link
                  key={a.agent_id}
                  to={`/agents/run?id=${a.agent_id}`}
                  className="border-2 border-slate-900 rounded-xl p-3 hover:shadow-brut transition-all"
                >
                  <div className="text-2xl mb-1">{a.emoji}</div>
                  <div className="font-heading font-bold text-sm truncate">{a.name}</div>
                  <div className="text-xs text-slate-500 truncate">{a.description}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
